import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch, Link, useNavigate } from '@tanstack/react-router';
import client from '@/api/client';
import type { Floor, Room, Bed, Hostel, Tenant } from '@/api/types';
import {
  ChevronDown, ChevronRight, Plus, Trash2, Pencil, BedDouble, Wrench,
  Users, Layers, Building2, X, LogOut, User, Receipt, Camera, Upload, Ban, AlertCircle, ScrollText, ArrowLeftRight, Move,
} from 'lucide-react';
import { SwapBedPanel } from '@/features/beds/SwapBedPanel';
import { TransferBedPanel } from '@/features/beds/TransferBedPanel';
import { SignOnBehalfFields, EMPTY_SIGN_ON_BEHALF, type SignOnBehalfValue } from '@/components/SignatureCapture';
import { Select } from '@/components/ui/Select';
import { sanitizePhoneInput } from '@/lib/utils';
import { useTenantPhoneLookup } from '@/features/tenants/useTenantPhoneLookup';
import { TenantPhoneMatchDialog } from '@/features/tenants/TenantPhoneMatchDialog';

interface HostelStats { activeTenants: number; reservedBeds: number; vacantBeds: number; checkedOutTenants: number; cancelledTenants: number; blockedBeds: number; }

const SHARING_OPTIONS = [
  { value: 'SINGLE', label: '1 Bed' },
  { value: 'DOUBLE', label: '2 Beds' },
  { value: 'TRIPLE', label: '3 Beds' },
  { value: 'FOUR',   label: '4 Beds' },
  { value: 'FIVE',   label: '5 Beds' },
  { value: 'SIX',    label: '6 Beds' },
  { value: 'CUSTOM', label: 'Custom' },
];

const FLOOR_NAMES = ['Ground Floor','First Floor','Second Floor','Third Floor',
  'Fourth Floor','Fifth Floor','Sixth Floor','Basement'];

const BED_STATUS_CONFIG = {
  VACANT:      { cls: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100', label: 'Free', labelKey: 'owner.bedFree' },
  OCCUPIED:    { cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', label: 'Occupied', labelKey: 'room.occupied' },
  RESERVED:    { cls: 'bg-amber-100 text-amber-800 border-amber-400 hover:bg-amber-200 ring-1 ring-amber-300', label: 'Reserved', labelKey: 'common.reserved' },
  MAINTENANCE: { cls: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200', label: 'Maint.', labelKey: 'owner.bedMaint' },
} as const;

// ── Bed card (interactive) ─────────────────────────────────
function BedCard({ bed, onClick, onDelete, onMaintenance }: {
  bed: Bed;
  onClick: () => void;
  onDelete?: () => void;
  onMaintenance?: () => void;
}) {
  const { t } = useTranslation();
  const c = BED_STATUS_CONFIG[bed.status] ?? BED_STATUS_CONFIG.VACANT;
  const showIcons = bed.status === 'VACANT' || bed.status === 'MAINTENANCE';
  return (
    <div className={`inline-flex items-center rounded-lg text-xs font-semibold border ${c.cls}`}>
      <button onClick={onClick} className="inline-flex items-center gap-1 px-2.5 py-1">
        {bed.bedLabel}
        <span className="font-normal opacity-70">{t(c.labelKey, c.label)}</span>
      </button>
      {showIcons && (
        <span className="inline-flex items-center gap-0.5 pr-1.5 pl-0.5 border-l border-current border-opacity-20">
          {onMaintenance && bed.status === 'VACANT' && (
            <button
              onClick={e => { e.stopPropagation(); onMaintenance(); }}
              className="p-0.5 rounded text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition"
              title={t('owner.markAsMaintenance', 'Mark as Maintenance')}
            >
              <Wrench size={11} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="p-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-100 transition"
              title={t('owner.deleteBed', 'Delete Bed')}
            >
              <Trash2 size={11} />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// ── Checkout form ──────────────────────────────────────────
function CheckoutForm({ onSubmit, isPending, onCancel }: {
  onSubmit: (d: { checkoutDate: string; notes?: string; refundDeposit?: boolean }) => void;
  isPending: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    checkoutDate: new Date().toISOString().split('T')[0],
    notes: '', refundDeposit: false,
  });
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
      <h3 className="font-semibold text-red-900 mb-3">{t('owner.confirmCheckOut', 'Confirm Check-Out')}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.checkOutDate', 'Check-Out Date')} *</label>
          <input type="date" value={form.checkoutDate}
            onChange={e => setForm(p => ({ ...p, checkoutDate: e.target.value }))}
            className="w-full border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2 self-end pb-1.5">
          <input type="checkbox" id="refund" checked={form.refundDeposit}
            onChange={e => setForm(p => ({ ...p, refundDeposit: e.target.checked }))} className="rounded" />
          <label htmlFor="refund" className="text-sm text-gray-700">{t('owner.refundSecurityDeposit', 'Refund security deposit')}</label>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.notes', 'Notes')}</label>
          <textarea rows={2} value={form.notes}
            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none" />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => onSubmit(form)} disabled={isPending}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
          {isPending ? t('owner.processing', 'Processing…') : t('owner.confirmCheckOut', 'Confirm Check-Out')}
        </button>
        <button onClick={onCancel} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

// ── Occupied bed view ──────────────────────────────────────
function OccupiedBedView({ bedId, room, bedLabel, onSuccess }: { bedId: string; room: Room; bedLabel: string; onSuccess: (tenantId?: string) => void }) {
  const { t } = useTranslation();
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const { data: tenant, isLoading } = useQuery<Tenant | null>({
    queryKey: ['bed-tenant', bedId],
    queryFn: () => client.get(`/api/beds/${bedId}/tenant`).then(r => r as unknown as Tenant | null),
  });

  const checkoutMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      client.post(`/api/tenants/${tenant!.id}/checkout`, body),
    onSuccess: () => onSuccess(),
  });

  if (isLoading) return <p className="text-gray-400 py-6 text-center">{t('owner.loadingTenantInfo', 'Loading tenant info…')}</p>;
  if (!tenant) return <p className="text-gray-500 py-6 text-center">{t('owner.noActiveTenantForBed', 'No active tenant found for this bed.')}</p>;

  return (
    <div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-5">
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.fullName')}</p><p className="font-semibold text-gray-900">{tenant.fullName}</p></div>
        <div><p className="text-xs text-gray-400 mb-0.5">{t('common.phone')}</p><p className="font-semibold">{tenant.phone}</p></div>
        {tenant.email && <div><p className="text-xs text-gray-400 mb-0.5">{t('common.email')}</p><p className="font-medium">{tenant.email}</p></div>}
        {tenant.gender && <div><p className="text-xs text-gray-400 mb-0.5">{t('owner.gender', 'Gender')}</p><p className="font-medium">{{ MALE: t('owner.male'), FEMALE: t('owner.female'), OTHER: t('owner.other') }[tenant.gender] ?? tenant.gender}</p></div>}
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.joiningDate')}</p><p className="font-medium">{tenant.joiningDate}</p></div>
        {tenant.expectedCheckoutDate && <div><p className="text-xs text-gray-400 mb-0.5">{t('owner.expectedCheckout', 'Expected Checkout')}</p><p className="font-medium">{tenant.expectedCheckoutDate}</p></div>}
        <div><p className="text-xs text-gray-400 mb-0.5">{t('tenant.monthlyRent')}</p><p className="font-semibold text-teal-700">{tenant.monthlyRent != null ? `₹${Number(tenant.monthlyRent).toLocaleString()}/mo` : '—'}</p></div>
        <div><p className="text-xs text-gray-400 mb-0.5">{t('owner.securityDeposit', 'Security Deposit')}</p><p className="font-medium">{tenant.securityDeposit != null ? `₹${Number(tenant.securityDeposit).toLocaleString()}` : '—'}</p></div>
        {tenant.notes && <div className="col-span-2"><p className="text-xs text-gray-400 mb-0.5">{t('owner.notes', 'Notes')}</p><p className="text-sm text-gray-700">{tenant.notes}</p></div>}
      </div>

      {!showCheckout && !showSwap && !showTransfer ? (
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/owner/hostels/$id/tenants/$tenantId"
            params={{ id: tenant.hostelId, tenantId: tenant.id }}
            search={{ tab: 'profile' }}
            title={t('owner.viewProfile', 'View Profile')}
            className="flex items-center gap-1.5 text-sm text-teal-600 border border-teal-300 px-3 py-1.5 rounded-lg hover:bg-teal-50 font-medium"
          >
            <User size={14} /> {t('tenant.profile')}
          </Link>
          <Link
            to="/owner/hostels/$id/tenants/$tenantId"
            params={{ id: tenant.hostelId, tenantId: tenant.id }}
            search={{ tab: 'rent' }}
            title={t('owner.viewPayments', 'View Payments')}
            className="flex items-center gap-1.5 text-sm text-indigo-600 border border-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-50 font-medium"
          >
            <Receipt size={14} /> {t('owner.payments', 'Payments')}
          </Link>
          <button onClick={() => setShowTransfer(true)}
            className="flex items-center gap-1.5 text-sm text-blue-600 border border-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 font-medium">
            <Move size={14} /> {t('owner.transferRoom', 'Transfer Room')}
          </button>
          <button onClick={() => setShowSwap(true)}
            className="flex items-center gap-1.5 text-sm text-violet-600 border border-violet-300 px-3 py-1.5 rounded-lg hover:bg-violet-50 font-medium">
            <ArrowLeftRight size={14} /> {t('owner.swapBed', 'Swap Bed')}
          </button>
          <button onClick={() => setShowCheckout(true)}
            className="flex items-center gap-1.5 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium">
            <LogOut size={14} /> {t('owner.checkOut', 'Check Out')}
          </button>
        </div>
      ) : showSwap ? (
        <SwapBedPanel
          hostelId={tenant.hostelId}
          lockedTenant={{ ...tenant, roomNumber: room.roomNumber, bedLabel }}
          onClose={() => setShowSwap(false)}
          onSuccess={() => onSuccess()}
        />
      ) : showTransfer ? (
        <TransferBedPanel
          tenant={{ ...tenant, roomNumber: room.roomNumber, bedLabel }}
          hostelId={tenant.hostelId}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => onSuccess()}
        />
      ) : (
        <CheckoutForm
          onSubmit={d => checkoutMutation.mutate(d as Record<string, unknown>)}
          isPending={checkoutMutation.isPending}
          onCancel={() => setShowCheckout(false)}
        />
      )}
      {checkoutMutation.isError && (
        <p className="text-red-600 text-sm mt-2">{(checkoutMutation.error as Error).message}</p>
      )}
    </div>
  );
}

// ── Compact photo capture (no tenantId needed — stores to state) ──
function PhotoCapture({ value, onChange, mode }: {
  value: string; onChange: (b64: string) => void; mode: 'square' | 'landscape';
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showCam, setShowCam] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const resize = (file: File): Promise<string> =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const c = document.createElement('canvas');
        if (mode === 'square') {
          c.width = 300; c.height = 300;
          const ctx = c.getContext('2d')!;
          const s = Math.min(img.width, img.height);
          ctx.drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 300, 300);
        } else {
          c.width = 800; c.height = 600;
          const ctx = c.getContext('2d')!;
          const scale = Math.min(800 / img.width, 600 / img.height);
          const sw = img.width * scale; const sh = img.height * scale;
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
          ctx.drawImage(img, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
        }
        URL.revokeObjectURL(url);
        resolve(c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9));
      };
      img.src = url;
    });

  useEffect(() => {
    if (stream && videoRef.current && showCam) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, showCam]);

  const openCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: mode === 'square'
          ? { facingMode: 'user', width: 640, height: 640 }
          : { facingMode: 'environment', width: 1280, height: 720 },
      });
      setStream(s);
      setShowCam(true);
    } catch { fileRef.current?.click(); }
  };

  const capture = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    if (mode === 'square') {
      c.width = 300; c.height = 300;
      const ctx = c.getContext('2d')!;
      const s = Math.min(v.videoWidth, v.videoHeight);
      ctx.drawImage(v, (v.videoWidth - s) / 2, (v.videoHeight - s) / 2, s, s, 0, 0, 300, 300);
    } else {
      c.width = 800; c.height = 600;
      const ctx = c.getContext('2d')!;
      const scale = Math.min(800 / v.videoWidth, 600 / v.videoHeight);
      const sw = v.videoWidth * scale; const sh = v.videoHeight * scale;
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 800, 600);
      ctx.drawImage(v, (800 - sw) / 2, (600 - sh) / 2, sw, sh);
    }
    const b64 = c.toDataURL('image/jpeg', mode === 'square' ? 0.85 : 0.9);
    stream?.getTracks().forEach(t => t.stop());
    setStream(null); setShowCam(false);
    onChange(b64);
  };

  return (
    <>
      <div className={`flex flex-col gap-1.5 ${mode === 'square' ? 'items-center' : ''}`}>
        {value ? (
          <div className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <img src={value} alt="Captured" className="w-full h-full object-contain" />
            <button type="button" onClick={() => onChange('')}
              className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">✕</button>
          </div>
        ) : (
          <div className={`rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 ${mode === 'square' ? 'w-20 h-20' : 'w-full'}`}
            style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined}>
            <Camera size={mode === 'square' ? 20 : 28} />
          </div>
        )}
        <div className="flex gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 text-[11px] border border-gray-300 px-2 py-1 rounded-md hover:bg-gray-50 text-gray-600">
            <Upload size={10} /> {t('owner.upload', 'Upload')}
          </button>
          <button type="button" onClick={openCam}
            className="flex items-center gap-1 text-[11px] border border-teal-300 text-teal-700 px-2 py-1 rounded-md hover:bg-teal-50">
            <Camera size={10} /> {t('owner.camera', 'Camera')}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
        onChange={async e => { const f = e.target.files?.[0]; if (f) { onChange(await resize(f)); } e.target.value = ''; }} />
      <canvas ref={canvasRef} className="hidden" />

      {showCam && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-chrome-page rounded-2xl overflow-hidden w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="font-semibold text-sm">{t('owner.capturePhoto', 'Capture Photo')}</p>
              <button type="button" onClick={() => { stream?.getTracks().forEach(t => t.stop()); setStream(null); setShowCam(false); }}
                className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
            </div>
            <div className="p-3 space-y-2">
              <video ref={videoRef} className={`w-full rounded-lg bg-black ${mode === 'square' ? 'aspect-square' : ''}`}
                style={mode === 'landscape' ? { aspectRatio: '4/3' } : undefined} muted playsInline />
              <button type="button" onClick={capture}
                className="w-full bg-teal-600 text-white py-2 rounded-xl text-sm font-semibold">
                📷 {t('owner.capture', 'Capture')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Reserved bed view ──────────────────────────────────────
function ReservedBedView({ bedId, onSuccess }: { bedId: string; room?: Room; onSuccess: (tenantId?: string) => void }) {
  const { t } = useTranslation();
  const [showCheckin, setShowCheckin] = useState(false);
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: tenant, isLoading } = useQuery<Tenant | null>({
    queryKey: ['bed-tenant', bedId],
    queryFn: () => client.get(`/api/beds/${bedId}/tenant`).then(r => r as unknown as Tenant | null),
  });

  const checkinMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      client.post(`/api/tenants/${tenant!.id}/checkin`, body),
    onSuccess: () => onSuccess(tenant!.id),
  });

  const cancelMutation = useMutation({
    mutationFn: () => client.delete(`/api/tenants/${tenant!.id}/reservation`),
    onSuccess: () => onSuccess(),
  });

  if (isLoading) return <p className="text-gray-400 py-6 text-center">{t('common.loading')}</p>;
  if (!tenant) return <p className="text-gray-500 py-6 text-center">{t('owner.noReservationForBed', 'No reservation found for this bed.')}</p>;

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 overflow-hidden">
        <p className="text-xs font-semibold text-amber-700 uppercase tracking-widest mb-2">{t('owner.reservedTenant', 'Reserved Tenant')}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('common.name')}</p><p className="font-semibold text-gray-900 break-words">{tenant.fullName}</p></div>
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('common.phone')}</p><p className="font-semibold break-all">{tenant.phone}</p></div>
          {tenant.email && <div className="col-span-2 min-w-0"><p className="text-xs text-gray-400">{t('common.email')}</p><p className="font-medium break-all">{tenant.email}</p></div>}
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('owner.expectedJoin', 'Expected Join')}</p><p className="font-medium">{tenant.joiningDate}</p></div>
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('tenant.monthlyRent')}</p><p className="font-semibold text-teal-700">{tenant.monthlyRent != null ? `₹${Number(tenant.monthlyRent).toLocaleString()}/mo` : '—'}</p></div>
          <div className="min-w-0"><p className="text-xs text-gray-400">{t('owner.advancePaid', 'Advance Paid')}</p><p className="font-medium">{tenant.securityDeposit != null ? `₹${Number(tenant.securityDeposit).toLocaleString()}` : '—'}</p></div>
          {tenant.notes && <div className="col-span-2 min-w-0"><p className="text-xs text-gray-400">{t('owner.notes', 'Notes')}</p><p className="text-sm break-words">{tenant.notes}</p></div>}
        </div>
      </div>

      {!showCheckin ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowCheckin(true)}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ✓ {t('owner.checkInTenant', 'Check In Tenant')}
          </button>
          <button onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}
            className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {cancelMutation.isPending ? t('owner.cancelling', 'Cancelling…') : `✕ ${t('owner.cancelReservation', 'Cancel Reservation')}`}
          </button>
        </div>
      ) : (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
          <p className="font-semibold text-teal-900 mb-3">{t('owner.confirmCheckIn', 'Confirm Check-In')}</p>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.actualJoiningDate', 'Actual Joining Date')} *</label>
            <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm w-48" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => checkinMutation.mutate({ joiningDate })} disabled={checkinMutation.isPending}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
              {checkinMutation.isPending ? t('owner.checkingIn', 'Checking In…') : t('owner.confirmCheckIn', 'Confirm Check-In')}
            </button>
            <button onClick={() => setShowCheckin(false)} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.back')}</button>
          </div>
          {checkinMutation.isError && <p className="text-red-600 text-sm mt-2">{(checkinMutation.error as Error).message}</p>}
        </div>
      )}
      {cancelMutation.isError && <p className="text-red-600 text-sm mt-2">{(cancelMutation.error as Error).message}</p>}
    </div>
  );
}

// ── Admit Tenant form ──────────────────────────────────────
function AdmitTenantForm({ hostelId, bedId, defaultRent, hostel, onSuccess, onCancel }: {
  hostelId: string; bedId: string; defaultRent: number; hostel: Hostel | null;
  onSuccess: (tenantId?: string) => void; onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'admit' | 'reserve'>('admit');
  const [onBehalf, setOnBehalf] = useState<SignOnBehalfValue>(EMPTY_SIGN_ON_BEHALF);
  const [form, setForm] = useState({
    fullName: '', phone: '', email: '', gender: '', dateOfBirth: '',
    occupation: '', companyOrInstitution: '',
    permanentAddress: '', city: '', state: '', pincode: '',
    govIdType: '',
    joiningDate: new Date().toISOString().split('T')[0],
    expectedCheckoutDate: '',
    monthlyRent: String(defaultRent), securityDeposit: '',
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRel: '',
    notes: '',
    fatherName: '', fatherContact: '', fatherOccupation: '',
    motherName: '', motherContact: '', motherOccupation: '',
  });
  const [photos, setPhotos] = useState({ photoUrl: '' });
  const [govIds, setGovIds] = useState([{ govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }]);
  const [reactivateTenantId, setReactivateTenantId] = useState<string | null>(null);
  const phoneLookup = useTenantPhoneLookup();
  const PHONE_KEYS = new Set(['phone', 'emergencyContactPhone', 'fatherContact', 'motherContact']);
  const f = (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm(p => ({ ...p, [key]: PHONE_KEYS.has(key as string) ? sanitizePhoneInput(e.target.value) : e.target.value }));
      if (key === 'phone') setReactivateTenantId(null);
    };

  async function handlePhoneBlur() {
    await phoneLookup.lookup(form.phone);
  }

  function handlePrefillBasic(fullName: string, email: string) {
    setForm(p => ({ ...p, fullName: p.fullName || fullName, email: p.email || email }));
    phoneLookup.reset();
  }

  function handleFetchDetails(tenant: Tenant) {
    setForm(p => ({
      ...p,
      fullName: tenant.fullName, phone: tenant.phone, email: tenant.email ?? '',
      gender: tenant.gender ?? '', dateOfBirth: tenant.dateOfBirth ?? '',
      occupation: tenant.occupation ?? '', companyOrInstitution: tenant.companyOrInstitution ?? '',
      permanentAddress: tenant.permanentAddress ?? '', city: tenant.city ?? '', state: tenant.state ?? '', pincode: tenant.pincode ?? '',
      govIdType: tenant.govIdType ?? '',
      monthlyRent: tenant.monthlyRent != null ? String(tenant.monthlyRent) : p.monthlyRent,
      securityDeposit: tenant.securityDeposit != null ? String(tenant.securityDeposit) : p.securityDeposit,
      emergencyContactName: tenant.emergencyContactName ?? '', emergencyContactPhone: tenant.emergencyContactPhone ?? '',
      emergencyContactRel: tenant.emergencyContactRel ?? '',
      fatherName: tenant.fatherName ?? '', fatherContact: tenant.fatherContact ?? '', fatherOccupation: tenant.fatherOccupation ?? '',
      motherName: tenant.motherName ?? '', motherContact: tenant.motherContact ?? '', motherOccupation: tenant.motherOccupation ?? '',
    }));
    setPhotos({ photoUrl: tenant.photoUrl ?? '' });
    setReactivateTenantId(tenant.id);
    phoneLookup.reset();
  }

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => client.post('/api/tenants', body),
    onSuccess: (data, variables) => {
      const created = data as unknown as Tenant;
      const isReserve = (variables as Record<string, unknown>).status === 'RESERVED';
      onSuccess(isReserve ? undefined : created.id);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      hostelId, bedId,
      fullName: form.fullName, phone: form.phone,
      email: form.email || undefined,
      dateOfBirth: form.dateOfBirth || undefined,
      gender: form.gender || undefined,
      govIdType: form.govIdType || undefined,
      joiningDate: form.joiningDate,
      expectedCheckoutDate: form.expectedCheckoutDate || undefined,
      monthlyRent: Number(form.monthlyRent),
      securityDeposit: form.securityDeposit ? Number(form.securityDeposit) : 0,
      emergencyContactName: form.emergencyContactName || undefined,
      emergencyContactPhone: form.emergencyContactPhone || undefined,
      occupation: form.occupation || undefined,
      companyOrInstitution: form.companyOrInstitution || undefined,
      permanentAddress: form.permanentAddress || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      pincode: form.pincode || undefined,
      emergencyContactRelation: form.emergencyContactRel || undefined,
      notes: form.notes || undefined,
      photoUrl: photos.photoUrl || undefined,
      govIds: govIds.filter(g => g.frontPhotoUrl || g.backPhotoUrl || g.govIdType).map(g => ({
        govIdType: g.govIdType || undefined,
        frontPhotoUrl: g.frontPhotoUrl || undefined,
        backPhotoUrl: g.backPhotoUrl || undefined,
      })),
      fatherName: form.fatherName || undefined,
      fatherContact: form.fatherContact || undefined,
      fatherOccupation: form.fatherOccupation || undefined,
      motherName: form.motherName || undefined,
      motherContact: form.motherContact || undefined,
      motherOccupation: form.motherOccupation || undefined,
      status: mode === 'reserve' ? 'RESERVED' : undefined,
      termsAccepted: (onBehalf.enabled && onBehalf.agreed && !!onBehalf.signature) || undefined,
      termsSignature: onBehalf.signature || undefined,
      reactivateTenantId: reactivateTenantId || undefined,
    });
  };

  const onBehalfIncomplete = onBehalf.enabled && (!onBehalf.agreed || !onBehalf.signature);
  const inp = 'w-full border rounded-lg px-3 py-2 text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {phoneLookup.result && (
        <TenantPhoneMatchDialog
          result={phoneLookup.result}
          role="owner"
          currentHostelId={hostelId}
          onFetchDetails={handleFetchDetails}
          onPrefillBasic={handlePrefillBasic}
          onStartFresh={() => phoneLookup.reset()}
          onClose={() => phoneLookup.reset()}
        />
      )}
      {/* Mode toggle */}
      <div className="flex rounded-lg border border-gray-200 overflow-hidden">
        <button type="button" onClick={() => setMode('admit')}
          className={`flex-1 py-2 text-sm font-medium transition ${mode === 'admit' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
          {t('owner.admitNow', 'Admit Now')}
        </button>
        <button type="button" onClick={() => setMode('reserve')}
          className={`flex-1 py-2 text-sm font-medium transition ${mode === 'reserve' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
          {t('owner.reserveBed', 'Reserve Bed')}
        </button>
      </div>
      {mode === 'reserve' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('owner.reserveBedHint', 'Bed will be marked Reserved. The tenant can check in later to activate the bed. Fill in the expected joining date and advance paid below.')}
        </p>
      )}

      {/* Profile Photo */}
      <div className="flex items-start gap-4 pb-4 border-b border-gray-100">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1.5">{t('owner.profilePhoto', 'Profile Photo')}</p>
          <PhotoCapture value={photos.photoUrl} onChange={v => setPhotos(p => ({ ...p, photoUrl: v }))} mode="square" />
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('tenant.fullName')} *</label>
            <input required value={form.fullName} onChange={f('fullName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')} *</label>
            <input type="tel" inputMode="tel" required value={form.phone} onChange={f('phone')} onBlur={() => void handlePhoneBlur()} className={inp} />
            {phoneLookup.loading ? (
              <p className="text-[11px] text-gray-400 mt-1">{t('tenant.checkingPhone', 'Checking…')}</p>
            ) : reactivateTenantId ? (
              <p className="text-[11px] text-teal-600 mt-1">{t('tenant.reactivatingPreviousStay', 'Reactivating this tenant’s previous profile.')}</p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1">{t('tenant.phoneSearchHint', 'Enter phone number to search for an existing profile.')}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.gender', 'Gender')}</label>
            <Select
              value={form.gender}
              onChange={f('gender')}
              options={[
                { value: '', label: t('owner.select', 'Select') },
                { value: 'MALE', label: t('owner.male', 'Male') },
                { value: 'FEMALE', label: t('owner.female', 'Female') },
                { value: 'OTHER', label: t('owner.other', 'Other') },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Personal */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.email')}</label>
          <input type="email" value={form.email} onChange={f('email')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.dateOfBirth', 'Date of Birth')}</label>
          <input type="date" value={form.dateOfBirth} onChange={f('dateOfBirth')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.occupation', 'Occupation')}</label>
          <input value={form.occupation} onChange={f('occupation')} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.companyOrInstitution', 'Company / Institution')}</label>
          <input value={form.companyOrInstitution} onChange={f('companyOrInstitution')} className={inp} />
        </div>
      </div>

      {/* Address */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('common.address')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.permanentAddress', 'Permanent Address')}</label>
            <textarea value={form.permanentAddress} onChange={f('permanentAddress')} rows={2} className={`${inp} resize-none`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.city', 'City')}</label>
            <input value={form.city} onChange={f('city')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.state', 'State')}</label>
            <input value={form.state} onChange={f('state')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.pincode', 'Pincode')}</label>
            <input value={form.pincode} onChange={f('pincode')} className={inp} />
          </div>
        </div>
      </div>

      {/* ID & Financials */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.idAndFinancials', 'ID & Financials')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.govIdType', 'Gov ID Type')}</label>
            <Select
              value={form.govIdType}
              onChange={f('govIdType')}
              options={[
                { value: '', label: '—' },
                ...['AADHAAR', 'PASSPORT', 'DRIVING_LICENSE', 'VOTER_ID', 'NATIONAL_ID', 'OTHER'].map(idType => ({ value: idType, label: idType })),
              ]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {mode === 'reserve' ? t('owner.expectedJoiningDate', 'Expected Joining Date') : t('tenant.joiningDate')} *
            </label>
            <input type="date" required value={form.joiningDate} onChange={f('joiningDate')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.monthlyRentRupee', 'Monthly Rent (₹)')} *</label>
            <input required type="number" value={form.monthlyRent} onChange={f('monthlyRent')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.securityDepositRupee', 'Security Deposit (₹)')}</label>
            <input type="number" value={form.securityDeposit} onChange={f('securityDeposit')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.expectedCheckout', 'Expected Checkout')}</label>
            <input type="date" value={form.expectedCheckoutDate} onChange={f('expectedCheckoutDate')} className={inp} />
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.emergencyContact', 'Emergency Contact')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.name')}</label>
            <input value={form.emergencyContactName} onChange={f('emergencyContactName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')}</label>
            <input type="tel" inputMode="tel" value={form.emergencyContactPhone} onChange={f('emergencyContactPhone')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.relation', 'Relation')}</label>
            <input value={form.emergencyContactRel} onChange={f('emergencyContactRel')} placeholder={t('owner.relationExample', 'e.g. Father, Spouse')} className={inp} />
          </div>
        </div>
      </div>

      {/* Government IDs */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.governmentIds', 'Government IDs')}</p>
        <div className="space-y-4">
          {govIds.map((g, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3 relative bg-gray-50/40">
              {govIds.length > 1 && (
                <button type="button" onClick={() => setGovIds(prev => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-3 right-3 text-gray-400 hover:text-red-500"><X size={14} /></button>
              )}
              <Select
                value={g.govIdType}
                onChange={e => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, govIdType: e.target.value } : x))}
                className="w-auto mr-6"
                options={[
                  { value: '', label: t('owner.idTypePlaceholder', '— ID Type —') },
                  ...['AADHAAR', 'PASSPORT', 'DRIVING_LICENSE', 'VOTER_ID', 'NATIONAL_ID', 'OTHER'].map(idType => ({ value: idType, label: idType })),
                ]}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">{t('owner.frontSide', 'Front Side')}</p>
                  <PhotoCapture value={g.frontPhotoUrl} onChange={v => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, frontPhotoUrl: v } : x))} mode="landscape" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1.5">{t('owner.backSide', 'Back Side')}</p>
                  <PhotoCapture value={g.backPhotoUrl} onChange={v => setGovIds(prev => prev.map((x, idx) => idx === i ? { ...x, backPhotoUrl: v } : x))} mode="landscape" />
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setGovIds(prev => [...prev, { govIdType: '', frontPhotoUrl: '', backPhotoUrl: '' }])}
            className="flex items-center gap-1.5 text-sm text-teal-700 border border-teal-300 px-3 py-1.5 rounded-lg hover:bg-teal-50">
            ＋ {t('owner.addAnotherId', 'Add Another ID')}
          </button>
        </div>
      </div>

      {/* Parent / Guardian */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{t('owner.parentGuardianDetails', 'Parent / Guardian Details')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 text-xs font-medium text-gray-500 -mb-1">{t('owner.fathersDetails', "Father's Details")}</div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.fathersName', "Father's Name")}</label>
            <input value={form.fatherName} onChange={f('fatherName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.fathersContact', "Father's Contact")}</label>
            <input type="tel" inputMode="tel" value={form.fatherContact} onChange={f('fatherContact')} className={inp} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.fathersOccupation', "Father's Occupation")}</label>
            <input value={form.fatherOccupation} onChange={f('fatherOccupation')} className={inp} />
          </div>
          <div className="col-span-2 text-xs font-medium text-gray-500 mt-1 -mb-1">{t('owner.mothersDetails', "Mother's Details")}</div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.mothersName', "Mother's Name")}</label>
            <input value={form.motherName} onChange={f('motherName')} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.mothersContact', "Mother's Contact")}</label>
            <input type="tel" inputMode="tel" value={form.motherContact} onChange={f('motherContact')} className={inp} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.mothersOccupation', "Mother's Occupation")}</label>
            <input value={form.motherOccupation} onChange={f('motherOccupation')} className={inp} />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.notes', 'Notes')}</label>
        <textarea value={form.notes} onChange={f('notes')} rows={2} className={`${inp} resize-none`} />
      </div>

      {/* Sign on behalf (no smartphone) */}
      <div className="border-t border-gray-100 pt-4">
        <SignOnBehalfFields hostel={hostel} value={onBehalf} onChange={setOnBehalf} />
      </div>

      {mutation.isError && <p className="text-red-600 text-sm">{(mutation.error as Error).message}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={mutation.isPending || onBehalfIncomplete}
          className={`px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 text-white ${mode === 'reserve' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-600 hover:bg-teal-700'}`}>
          {mutation.isPending
            ? (mode === 'reserve' ? t('owner.reserving', 'Reserving…') : t('owner.admitting', 'Admitting…'))
            : (mode === 'reserve' ? t('owner.reserveBed', 'Reserve Bed') : t('owner.admitTenant', 'Admit Tenant'))}
        </button>
        <button type="button" onClick={onCancel} className="border px-5 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
    </form>
  );
}

// ── Bed modal (page-level overlay) ─────────────────────────
function BedModal({ bed, room, hostelId, hostel, onClose }: {
  bed: Bed; room: Room; hostelId: string; hostel: Hostel | null; onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showMaintenanceReason, setShowMaintenanceReason] = useState(false);
  const [maintenanceReason, setMaintenanceReason] = useState('');

  const handleSuccess = (tenantId?: string) => {
    void qc.invalidateQueries({ queryKey: ['beds', room.id] });
    void qc.invalidateQueries({ queryKey: ['bed-tenant', bed.id] });
    onClose();
    if (tenantId) {
      void navigate({
        to: '/owner/hostels/$id/tenants/$tenantId',
        params: { id: hostelId, tenantId },
        search: { tab: 'rent' },
      });
    }
  };

  const deleteBedMutation = useMutation({
    mutationFn: () => client.delete(`/api/beds/${bed.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      onClose();
    },
  });

  const markMaintenanceMutation = useMutation({
    mutationFn: (payload: { status: 'MAINTENANCE' | 'VACANT'; maintenanceNotes?: string }) =>
      client.patch(`/api/beds/${bed.id}/status`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      setShowMaintenanceReason(false);
      setMaintenanceReason('');
      onClose();
    },
  });

  const handleDeleteBed = () => {
    if (window.confirm(t('owner.confirmDeleteBed', 'Delete this bed? This cannot be undone.'))) {
      deleteBedMutation.mutate();
    }
  };

  const statusLabelKeys: Record<string, [string, string]> = {
    VACANT: ['owner.admitTenant', 'Admit Tenant'], RESERVED: ['owner.reservedBed', 'Reserved Bed'],
    OCCUPIED: ['owner.tenantProfile', 'Tenant Profile'], MAINTENANCE: ['owner.bedMaintenance', 'Bed Maintenance'],
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-chrome-page rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {statusLabelKeys[bed.status] ? t(statusLabelKeys[bed.status][0], statusLabelKeys[bed.status][1]) : bed.status}
            </h2>
            <p className="text-sm text-gray-500">{t('common.room')} {room.roomNumber} · {t('common.bed')} {bed.bedLabel}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {bed.status === 'OCCUPIED' ? (
            <>
              <OccupiedBedView bedId={bed.id} room={room} bedLabel={bed.bedLabel} onSuccess={handleSuccess} />
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                {t('owner.bedHasTenantCannotDelete', 'This bed has a tenant assigned. Move them to another bed (Swap Bed) or check them out, then delete the bed.')}
              </p>
            </>
          ) : bed.status === 'RESERVED' ? (
            <>
              <ReservedBedView bedId={bed.id} room={room} onSuccess={handleSuccess} />
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                {t('owner.bedHasTenantCannotDelete', 'This bed has a tenant assigned. Move them to another bed (Swap Bed) or check them out, then delete the bed.')}
              </p>
            </>
          ) : bed.status === 'MAINTENANCE' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BedDouble size={28} className="text-gray-400" />
              </div>
              <p className="font-medium text-gray-700 mb-1">{t('owner.bedUnderMaintenance', 'Bed {{bedLabel}} is under maintenance', { bedLabel: bed.bedLabel })}</p>
              {bed.maintenanceNotes && (
                <p className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mb-3 text-left whitespace-pre-wrap">
                  <span className="font-medium text-gray-700">{t('owner.maintenanceReason', 'Reason')}: </span>{bed.maintenanceNotes}
                </p>
              )}
              <p className="text-sm text-gray-500 mb-4">{t('owner.markVacantHint', 'Mark as vacant to make it available for admission.')}</p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => markMaintenanceMutation.mutate({ status: 'VACANT' })}
                  disabled={markMaintenanceMutation.isPending}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {t('owner.markAsVacant', 'Mark as Vacant')}
                </button>
                <button
                  onClick={handleDeleteBed}
                  disabled={deleteBedMutation.isPending}
                  className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <Trash2 size={14} /> {t('owner.deleteBed', 'Delete Bed')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <AdmitTenantForm
                hostelId={hostelId}
                bedId={bed.id}
                defaultRent={room.rentAmount}
                hostel={hostel}
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
              <div className="mt-4 pt-4 border-t border-gray-100">
                {showMaintenanceReason ? (
                  <div className="max-w-sm mx-auto">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('owner.maintenanceReasonPrompt', 'Why is this bed going under maintenance?')}
                    </label>
                    <textarea
                      value={maintenanceReason}
                      onChange={e => setMaintenanceReason(e.target.value)}
                      rows={2}
                      placeholder={t('owner.maintenanceReasonPlaceholder', 'e.g. Broken bed frame, pest control, painting…')}
                      className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    />
                    {markMaintenanceMutation.isError && (
                      <p className="text-red-600 text-xs mt-1">{(markMaintenanceMutation.error as Error).message}</p>
                    )}
                    <div className="flex gap-2 justify-center mt-2">
                      <button
                        onClick={() => markMaintenanceMutation.mutate({ status: 'MAINTENANCE', maintenanceNotes: maintenanceReason })}
                        disabled={markMaintenanceMutation.isPending || !maintenanceReason.trim()}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                      >
                        {markMaintenanceMutation.isPending ? t('common.loading') : t('owner.confirmMaintenance', 'Confirm')}
                      </button>
                      <button
                        onClick={() => { setShowMaintenanceReason(false); setMaintenanceReason(''); }}
                        className="border px-4 py-1.5 rounded-lg text-xs"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setShowMaintenanceReason(true)}
                      className="inline-flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800"
                    >
                      <Wrench size={12} /> {t('owner.markAsMaintenance', 'Mark as Maintenance')}
                    </button>
                    <span className="text-gray-200">|</span>
                    <button
                      onClick={handleDeleteBed}
                      disabled={deleteBedMutation.isPending}
                      className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 size={12} /> {t('owner.deleteThisBed', 'Delete this bed')}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {deleteBedMutation.isError && (
            <p className="text-red-600 text-sm mt-2">{(deleteBedMutation.error as Error).message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Room card ──────────────────────────────────────────────
function RoomCard({ room, onDelete, onBedClick }: {
  room: Room;
  onDelete: (id: string) => void;
  onBedClick: (bed: Bed) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: beds = [] } = useQuery<Bed[]>({
    queryKey: ['beds', room.id],
    queryFn: () => client.get(`/api/rooms/${room.id}/beds`).then(r => r as unknown as Bed[]),
    enabled: open,
  });

  const addBedMutation = useMutation({
    mutationFn: () => client.post(`/api/rooms/${room.id}/beds`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });

  const deleteBedMutation = useMutation({
    mutationFn: (bedId: string) => client.delete(`/api/beds/${bedId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });

  const setBedStatusMutation = useMutation({
    mutationFn: ({ bedId, status, maintenanceNotes }: { bedId: string; status: string; maintenanceNotes?: string }) =>
      client.patch(`/api/beds/${bedId}/status`, { status, maintenanceNotes }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['beds', room.id] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
    },
  });

  const handleDeleteBed = (bed: Bed) => {
    if (window.confirm(`Delete bed ${bed.bedLabel}? This cannot be undone.`)) {
      deleteBedMutation.mutate(bed.id);
    }
  };

  const handleMarkMaintenance = (bed: Bed) => {
    const reason = window.prompt(t('owner.maintenanceReasonPrompt', 'Why is this bed going under maintenance?'));
    if (reason === null) return;
    if (!reason.trim()) { window.alert(t('owner.maintenanceReasonRequired', 'A reason is required to mark a bed as under maintenance.')); return; }
    setBedStatusMutation.mutate({ bedId: bed.id, status: 'MAINTENANCE', maintenanceNotes: reason.trim() });
  };

  const occupied = beds.filter(b => b.status === 'OCCUPIED').length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer transition"
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronDown size={15} className="text-gray-400 shrink-0" /> : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
        <BedDouble size={15} className="text-teal-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900">{t('common.room')} {room.roomNumber}</span>
          {room.roomName && <span className="text-gray-500 text-sm ml-1.5">— {room.roomName}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
          <span>{t('owner.occupiedOfTotal', '{{occupied}}/{{total}} occupied', { occupied, total: room.totalBeds })}</span>
          <span className="font-medium text-teal-700">₹{Number(room.rentAmount).toLocaleString()}/mo</span>
          <span className={`px-2 py-0.5 rounded-full font-medium
            ${room.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {room.status}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(room.id); }}
            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            {beds.length === 0 && <span className="text-xs text-gray-400">{t('owner.noBedsFound', 'No beds found')}</span>}
            {beds.map(b => (
              <BedCard
                key={b.id}
                bed={b}
                onClick={() => onBedClick(b)}
                onDelete={() => handleDeleteBed(b)}
                onMaintenance={() => handleMarkMaintenance(b)}
              />
            ))}
            <button
              onClick={() => addBedMutation.mutate()}
              disabled={addBedMutation.isPending}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-dashed border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600 transition disabled:opacity-50"
            >
              <Plus size={12} /> {t('owner.addBed', 'Add Bed')}
            </button>
          </div>
          {addBedMutation.isError && <p className="text-red-600 text-xs mt-2">{(addBedMutation.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}

// ── Create-Room form ───────────────────────────────────────
function CreateRoomForm({ floorId, onDone }: { floorId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    roomNumber: '', roomName: '', sharingType: 'DOUBLE', customBedCount: '4',
    rentAmount: '', depositAmount: '',
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => client.post('/api/rooms', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rooms', floorId] });
      onDone();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      floorId,
      roomNumber: form.roomNumber,
      roomName: form.roomName || undefined,
      sharingType: form.sharingType,
      customBedCount: form.sharingType === 'CUSTOM' ? Number(form.customBedCount) : undefined,
      rentAmount: Number(form.rentAmount),
      depositAmount: form.depositAmount ? Number(form.depositAmount) : 0,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-teal-50 border border-teal-200 rounded-lg mt-2 grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('room.roomNumber')} *</label>
        <input required value={form.roomNumber} onChange={e => setForm(p => ({ ...p, roomNumber: e.target.value }))}
          placeholder="101" className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.roomName', 'Room Name')}</label>
        <input value={form.roomName} onChange={e => setForm(p => ({ ...p, roomName: e.target.value }))}
          placeholder={t('owner.roomNameExample', 'e.g. Corner Suite')} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.sharingCapacity', 'Sharing Capacity')} *</label>
        <Select
          value={form.sharingType}
          onChange={e => setForm(p => ({ ...p, sharingType: e.target.value }))}
          options={SHARING_OPTIONS.map(o => ({ value: o.value, label: t(`owner.sharing.${o.value}`, o.label) }))}
        />
      </div>
      {form.sharingType === 'CUSTOM' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.numberOfBeds', 'Number of Beds')} *</label>
          <input type="number" min="1" max="20" value={form.customBedCount}
            onChange={e => setForm(p => ({ ...p, customBedCount: e.target.value }))}
            className="w-full border rounded-lg px-3 py-1.5 text-sm" />
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.monthlyRentRupee', 'Monthly Rent (₹)')} *</label>
        <input required type="number" value={form.rentAmount}
          onChange={e => setForm(p => ({ ...p, rentAmount: e.target.value }))}
          placeholder="5000" className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.securityDepositRupee', 'Security Deposit (₹)')}</label>
        <input type="number" value={form.depositAmount}
          onChange={e => setForm(p => ({ ...p, depositAmount: e.target.value }))}
          placeholder="10000" className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div className="col-span-2 flex gap-2">
        <button type="submit" disabled={mutation.isPending}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
          {mutation.isPending ? t('owner.creating', 'Creating…') : t('owner.createRoomGenerateBeds', 'Create Room & Generate Beds')}
        </button>
        <button type="button" onClick={onDone} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
      {mutation.isError && <p className="col-span-2 text-red-600 text-sm">{(mutation.error as Error).message}</p>}
    </form>
  );
}

// ── Floor section ──────────────────────────────────────────
function FloorSection({ floor, hostelId, onDeleteFloor, onBedClick }: {
  floor: Floor;
  hostelId: string;
  onDeleteFloor: (id: string) => void;
  onBedClick: (bed: Bed, room: Room) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [addingRoom, setAddingRoom] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(floor.floorName);

  const { data: rooms = [] } = useQuery<Room[]>({
    queryKey: ['rooms', floor.id],
    queryFn: () => client.get(`/api/rooms?floorId=${floor.id}`).then(r => r as unknown as Room[]),
  });

  const updateFloor = useMutation({
    mutationFn: (body: { floorName: string }) => client.put(`/api/floors/${floor.id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['floors', hostelId] });
      setEditing(false);
    },
  });

  const deleteRoom = useMutation({
    mutationFn: (id: string) => client.delete(`/api/rooms/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rooms', floor.id] }),
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <button onClick={() => setOpen(o => !o)} className="text-gray-500 hover:text-gray-700">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <Layers size={16} className="text-blue-500 shrink-0" />
        {editing ? (
          <form onSubmit={e => { e.preventDefault(); updateFloor.mutate({ floorName: editName }); }}
            className="flex items-center gap-2 flex-1">
            <input value={editName} onChange={e => setEditName(e.target.value)} autoFocus
              className="border rounded px-2 py-1 text-sm flex-1" />
            <button type="submit" className="text-xs bg-blue-600 text-white px-3 py-1 rounded">{t('common.save')}</button>
            <button type="button" onClick={() => setEditing(false)} className="text-xs border px-3 py-1 rounded">{t('common.cancel')}</button>
          </form>
        ) : (
          <>
            <span className="font-semibold text-gray-800 flex-1">{floor.floorName}</span>
            <span className="text-xs text-gray-500 mr-2">{t('owner.roomCount', '{{count}} rooms', { count: rooms.length })}</span>
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600">
              <Pencil size={13} />
            </button>
            <button onClick={() => onDeleteFloor(floor.id)}
              className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
              <Trash2 size={13} />
            </button>
          </>
        )}
      </div>

      {open && (
        <div className="p-4 space-y-2">
          {rooms.map(r => (
            <RoomCard
              key={r.id}
              room={r}
              onDelete={id => deleteRoom.mutate(id)}
              onBedClick={b => onBedClick(b, r)}
            />
          ))}
          {rooms.length === 0 && !addingRoom && (
            <p className="text-sm text-gray-400 py-2">{t('owner.noRoomsOnFloor', 'No rooms on this floor yet.')}</p>
          )}
          {addingRoom
            ? <CreateRoomForm floorId={floor.id} onDone={() => setAddingRoom(false)} />
            : (
              <button onClick={() => setAddingRoom(true)}
                className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-medium mt-1">
                <Plus size={14} /> {t('room.addRoom')}
              </button>
            )
          }
        </div>
      )}
    </div>
  );
}

// ── Create-Floor form ──────────────────────────────────────
function CreateFloorForm({ hostelId, onDone }: { hostelId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({ floorName: '', floorNumber: '0', description: '' });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => client.post('/api/floors', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['floors', hostelId] });
      onDone();
    },
  });

  return (
    <form onSubmit={e => { e.preventDefault(); mutation.mutate({ hostelId, ...form, floorNumber: Number(form.floorNumber) }); }}
      className="bg-blue-50 border border-blue-200 rounded-xl p-4 grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.floorName', 'Floor Name')} *</label>
        <input list="floor-names" required value={form.floorName}
          onChange={e => setForm(p => ({ ...p, floorName: e.target.value }))}
          placeholder={t('owner.floorNameExample', 'e.g. Ground Floor')} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
        <datalist id="floor-names">
          {FLOOR_NAMES.map(n => <option key={n} value={t(`owner.floorNames.${n}`, n)} />)}
        </datalist>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.floorNumber', 'Floor Number')} *</label>
        <input type="number" required value={form.floorNumber}
          onChange={e => setForm(p => ({ ...p, floorNumber: e.target.value }))}
          className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.descriptionOptional', 'Description (optional)')}</label>
        <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          className="w-full border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <div className="col-span-2 flex gap-2">
        <button type="submit" disabled={mutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
          {mutation.isPending ? t('owner.creating', 'Creating…') : t('owner.createFloor', 'Create Floor')}
        </button>
        <button type="button" onClick={onDone} className="border px-4 py-1.5 rounded-lg text-sm">{t('common.cancel')}</button>
      </div>
      {mutation.isError && <p className="col-span-2 text-red-600 text-sm">{(mutation.error as Error).message}</p>}
    </form>
  );
}

// ── Vacant Beds Panel ─────────────────────────────────────
function VacantBedsPanel({ hostelId, onClose, onBedClick }: { hostelId: string; onClose: () => void; onBedClick: (bed: Bed, room: Room) => void }) {
  const { t } = useTranslation();
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['rooms-all', hostelId],
    queryFn: () => client.get(`/api/rooms?hostelId=${hostelId}`).then(r => r as unknown as Room[]),
  });

  const bedResults = useQueries({
    queries: rooms.map(r => ({
      queryKey: ['beds', r.id],
      queryFn: () => client.get(`/api/rooms/${r.id}/beds`).then(res => res as unknown as Bed[]),
    })),
  });

  const isLoading = roomsLoading || bedResults.some(r => r.isLoading);

  const vacantGroups = rooms
    .map((room, i) => ({
      room,
      vacantBeds: (bedResults[i]?.data ?? []).filter(b => b.status === 'VACANT'),
    }))
    .filter(g => g.vacantBeds.length > 0);

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-green-900 flex items-center gap-2">
          <BedDouble size={16} className="text-green-600" />
          {t('owner.vacantBeds', 'Vacant Beds')}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400">{t('owner.loadingVacantBeds', 'Loading vacant beds…')}</p>
      ) : vacantGroups.length === 0 ? (
        <p className="text-sm text-gray-500">{t('owner.noVacantBeds', 'No vacant beds at this hostel.')}</p>
      ) : (
        <div className="space-y-2">
          {vacantGroups.map(({ room, vacantBeds }) => (
            <div key={room.id} className="bg-white rounded-lg p-3 border border-green-100">
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-medium text-gray-800">
                  {t('common.room')} {room.roomNumber}
                  {room.roomName && <span className="text-gray-500 font-normal ml-1.5">— {room.roomName}</span>}
                  {room.floorNumber != null && (
                    <span className="text-xs text-gray-400 ml-2">{t('room.floor')} {room.floorNumber}</span>
                  )}
                </p>
                <span className="text-xs font-semibold text-teal-700">₹{Number(room.rentAmount).toLocaleString()}/mo</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {vacantBeds.map(b => (
                  <button
                    key={b.id}
                    onClick={() => { onClose(); onBedClick(b, room); }}
                    className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold hover:bg-green-200 hover:ring-1 hover:ring-green-400 transition"
                  >
                    {t('owner.bedAdmit', 'Bed {{bedLabel}} · Admit', { bedLabel: b.bedLabel })}
                  </button>
                ))}
                <span className="text-xs text-gray-500 self-center ml-1">
                  {t('owner.bedsFreeOfTotal', '{{free}} of {{total}} beds free', { free: vacantBeds.length, total: room.totalBeds })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MaintenanceBedsPanel({ hostelId, onClose, onBedClick }: { hostelId: string; onClose: () => void; onBedClick: (bed: Bed, room: Room) => void }) {
  const { t } = useTranslation();
  const { data: rooms = [], isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['rooms-all', hostelId],
    queryFn: () => client.get(`/api/rooms?hostelId=${hostelId}`).then(r => r as unknown as Room[]),
  });

  const bedResults = useQueries({
    queries: rooms.map(r => ({
      queryKey: ['beds', r.id],
      queryFn: () => client.get(`/api/rooms/${r.id}/beds`).then(res => res as unknown as Bed[]),
    })),
  });

  const isLoading = roomsLoading || bedResults.some(r => r.isLoading);

  const maintenanceGroups = rooms
    .map((room, i) => ({
      room,
      maintenanceBeds: (bedResults[i]?.data ?? []).filter(b => b.status === 'MAINTENANCE'),
    }))
    .filter(g => g.maintenanceBeds.length > 0);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Wrench size={16} className="text-gray-500" />
          {t('owner.maintenanceBeds', 'Maintenance Beds')}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400">{t('owner.loadingMaintenanceBeds', 'Loading maintenance beds…')}</p>
      ) : maintenanceGroups.length === 0 ? (
        <p className="text-sm text-gray-500">{t('owner.noMaintenanceBeds', 'No beds under maintenance at this hostel.')}</p>
      ) : (
        <div className="space-y-2">
          {maintenanceGroups.map(({ room, maintenanceBeds }) => (
            <div key={room.id} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-1.5">
                <p className="font-medium text-gray-800">
                  {t('common.room')} {room.roomNumber}
                  {room.roomName && <span className="text-gray-500 font-normal ml-1.5">— {room.roomName}</span>}
                  {room.floorNumber != null && (
                    <span className="text-xs text-gray-400 ml-2">{t('room.floor')} {room.floorNumber}</span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {maintenanceBeds.map(b => (
                  <button
                    key={b.id}
                    onClick={() => { onClose(); onBedClick(b, room); }}
                    className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded text-xs font-semibold hover:bg-gray-300 hover:ring-1 hover:ring-gray-400 transition"
                  >
                    {t('owner.bedManage', 'Bed {{bedLabel}} · Manage', { bedLabel: b.bedLabel })}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export function OwnerHostelDetailPage() {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ from: '/protected/owner/hostels/$id' });
  const { bedStatus } = useSearch({ from: '/protected/owner/hostels/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [addingFloor, setAddingFloor] = useState(false);
  const [showVacant, setShowVacant] = useState(bedStatus === 'VACANT');
  const [showMaintenance, setShowMaintenance] = useState(bedStatus === 'MAINTENANCE');
  const [bedModal, setBedModal] = useState<{ bed: Bed; room: Room } | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesText, setRulesText] = useState('');

  const { data: hostel } = useQuery<Hostel>({
    queryKey: ['hostel', hostelId],
    queryFn: () => client.get(`/api/hostels/${hostelId}`).then(r => r as unknown as Hostel),
  });

  const { data: floors = [], isLoading } = useQuery<Floor[]>({
    queryKey: ['floors', hostelId],
    queryFn: () => client.get(`/api/floors?hostelId=${hostelId}`).then(r => r as unknown as Floor[]),
  });

  const { data: stats } = useQuery<HostelStats>({
    queryKey: ['owner-dashboard', hostelId],
    queryFn: () => client.get(`/api/dashboard/owner/${hostelId}`).then(r => r as unknown as HostelStats),
  });

  const s = stats ?? { activeTenants: 0, reservedBeds: 0, vacantBeds: 0, checkedOutTenants: 0, cancelledTenants: 0, blockedBeds: 0 };

  const deleteFloor = useMutation({
    mutationFn: (id: string) => client.delete(`/api/floors/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['floors', hostelId] }),
  });

  const updateRules = useMutation({
    mutationFn: (rules: string) => client.put(`/api/hostels/${hostelId}/rules`, { rules }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hostel', hostelId] });
      setRulesOpen(false);
    },
  });

  useEffect(() => {
    if (hostel?.rules !== undefined) setRulesText(hostel.rules ?? '');
  }, [hostel?.rules]);

  function handleBedClick(bed: Bed, room: Room) {
    if (bed.status === 'OCCUPIED') {
      void client.get(`/api/beds/${bed.id}/tenant`).then((tenant: unknown) => {
        const t = tenant as Tenant | null;
        if (t) {
          void navigate({ to: '/owner/hostels/$id/tenants/$tenantId', params: { id: hostelId, tenantId: t.id }, search: { tab: 'profile' } });
        } else {
          setBedModal({ bed, room });
        }
      });
      return;
    }
    setBedModal({ bed, room });
  }

  return (
    <div>
      {/* Breadcrumb + hostel header */}
      <div className="flex items-center gap-3 mb-1">
        <Link to="/owner/hostels" search={{ status: '' }} className="text-teal-600 text-sm hover:underline">← {t('nav.hostels')}</Link>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-500">{hostel?.name ?? '…'}</span>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 size={22} className="text-teal-600" />
          {hostel?.name ?? t('owner.hostel', 'Hostel')}
        </h1>
        {hostel && <p className="text-sm text-gray-500 mt-0.5">{hostel.city}, {hostel.state}</p>}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-6 gap-1.5 mb-4">
        <button
          onClick={() => void navigate({ to: '/owner/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'ACTIVE' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-teal-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-teal-50 flex items-center justify-center mb-1 group-hover:bg-teal-100 transition">
            <Users size={12} className="text-teal-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.activeTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.active')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/owner/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'RESERVED' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-amber-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-amber-50 flex items-center justify-center mb-1 group-hover:bg-amber-100 transition">
            <BedDouble size={12} className="text-amber-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.reservedBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.reserved')}</p>
        </button>
        <button
          onClick={() => setShowVacant(v => !v)}
          className={`bg-white rounded-lg p-2 shadow-sm border flex flex-col items-center text-center hover:border-green-300 hover:shadow-md transition group ${showVacant ? 'border-green-400 ring-1 ring-green-300' : 'border-gray-100'}`}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center mb-1 transition ${showVacant ? 'bg-green-100' : 'bg-green-50 group-hover:bg-green-100'}`}>
            <BedDouble size={12} className="text-green-600" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.vacantBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('owner.vacant', 'Vacant')}</p>
        </button>
        <button
          onClick={() => setShowMaintenance(v => !v)}
          className={`bg-white rounded-lg p-2 shadow-sm border flex flex-col items-center text-center hover:border-gray-300 hover:shadow-md transition group ${showMaintenance ? 'border-gray-400 ring-1 ring-gray-300' : 'border-gray-100'}`}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center mb-1 transition ${showMaintenance ? 'bg-gray-200' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
            <Wrench size={12} className="text-gray-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.blockedBeds}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('owner.maintenance', 'Maint.')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/owner/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'CHECKED_OUT' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-gray-300 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center mb-1 group-hover:bg-gray-200 transition">
            <LogOut size={12} className="text-gray-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.checkedOutTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.checkedOut')}</p>
        </button>
        <button
          onClick={() => void navigate({ to: '/owner/hostels/$id/tenants', params: { id: hostelId }, search: { status: 'CANCELLED' } })}
          className="bg-white rounded-lg p-2 shadow-sm border border-gray-100 flex flex-col items-center text-center hover:border-red-200 hover:shadow-md transition group"
        >
          <div className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center mb-1 group-hover:bg-red-100 transition">
            <Ban size={12} className="text-red-500" />
          </div>
          <p className="text-sm font-bold text-gray-900 leading-none">{s.cancelledTenants}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{t('common.cancelled')}</p>
        </button>
      </div>

      {/* Vacant Beds Panel */}
      {showVacant && (
        <VacantBedsPanel
          hostelId={hostelId}
          onClose={() => setShowVacant(false)}
          onBedClick={(bed, room) => setBedModal({ bed, room })}
        />
      )}

      {/* Maintenance Beds Panel */}
      {showMaintenance && (
        <MaintenanceBedsPanel
          hostelId={hostelId}
          onClose={() => setShowMaintenance(false)}
          onBedClick={(bed, room) => setBedModal({ bed, room })}
        />
      )}

      {/* Quick actions */}
      <div className="flex gap-2 mb-4">
        <Link
          to="/owner/hostels/$id/complaints"
          params={{ id: hostelId }}
          className="flex items-center gap-1.5 text-sm text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg font-medium transition"
        >
          <AlertCircle size={14} /> {t('complaint.title')}
        </Link>
        <Link
          to="/owner/hostels/$id/rent"
          params={{ id: hostelId }}
          className="flex items-center gap-1.5 text-sm text-teal-600 border border-teal-200 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg font-medium transition"
        >
          <Receipt size={14} /> {t('nav.rent')}
        </Link>
      </div>

      {/* Rules & Regulations editor */}
      <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
          onClick={() => setRulesOpen(v => !v)}
        >
          <span className="font-semibold text-gray-800 text-sm flex items-center gap-2">
            <ScrollText size={15} className="text-amber-600" /> {t('owner.rulesAndRegulations', 'Rules & Regulations')}
            {hostel?.rules ? (
              <span className="text-xs font-normal text-gray-400">{t('owner.charsCount', '({{count}} chars)', { count: hostel.rules.length })}</span>
            ) : (
              <span className="text-xs font-normal text-amber-600">{t('owner.notSet', 'not set')}</span>
            )}
          </span>
          {rulesOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </button>
        {rulesOpen && (
          <div className="p-4 space-y-3">
            <p className="text-xs text-gray-500">{t('owner.rulesShownToTenantsHint', 'These rules are shown to tenants when they log in and must acknowledge before accessing their portal.')}</p>
            <textarea
              value={rulesText}
              onChange={e => setRulesText(e.target.value)}
              rows={8}
              placeholder={t('owner.rulesPlaceholder', 'Enter hostel rules and regulations here…\n\ne.g.\n1. No guests after 10 PM\n2. Keep common areas clean\n3. No smoking inside premises')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono resize-y"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => updateRules.mutate(rulesText)}
                disabled={updateRules.isPending}
                className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                {updateRules.isPending ? t('owner.saving', 'Saving…') : t('owner.saveRules', 'Save Rules')}
              </button>
              <button onClick={() => setRulesOpen(false)} className="border px-4 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
              {updateRules.isError && <p className="text-red-600 text-sm">{(updateRules.error as Error).message}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Floors section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Layers size={18} className="text-blue-500" />
          {t('owner.floorsAndRooms', 'Floors & Rooms')}
          <span className="text-sm font-normal text-gray-400">{t('owner.floorCount', '({{count}} floors)', { count: floors.length })}</span>
        </h2>
        {!addingFloor && (
          <button onClick={() => setAddingFloor(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
            <Plus size={14} /> {t('owner.addFloor', 'Add Floor')}
          </button>
        )}
      </div>

      {addingFloor && (
        <div className="mb-4">
          <CreateFloorForm hostelId={hostelId} onDone={() => setAddingFloor(false)} />
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-400 py-8 text-center">{t('owner.loadingFloors', 'Loading floors…')}</p>
      ) : floors.length === 0 ? (
        <div className="bg-blue-50 border-2 border-dashed border-blue-200 rounded-xl p-10 text-center">
          <Layers size={36} className="mx-auto text-blue-300 mb-3" />
          <p className="font-medium text-gray-700">{t('owner.noFloorsYet', 'No floors yet')}</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">{t('owner.startByAddingFloor', 'Start by adding a floor, then create rooms within it.')}</p>
          <button onClick={() => setAddingFloor(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium">
            {t('owner.addFirstFloor', 'Add First Floor')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {floors.map(f => (
            <FloorSection
              key={f.id}
              floor={f}
              hostelId={hostelId}
              onDeleteFloor={id => deleteFloor.mutate(id)}
              onBedClick={handleBedClick}
            />
          ))}
        </div>
      )}

      {/* Bed action modal */}
      {bedModal && (
        <BedModal
          bed={bedModal.bed}
          room={bedModal.room}
          hostelId={hostelId}
          hostel={hostel ?? null}
          onClose={() => setBedModal(null)}
        />
      )}
    </div>
  );
}
